import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Sparkles, Plus, X, UserPlus, Loader2, AlertCircle } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useState } from "react";

export interface AiSuggestion {
  id: string;
  title: string;
  description?: string | null;
  source: "email" | "meeting";
  sourceId?: string | null;
  quotedText?: string | null;
  suggestedDueDate?: string | null;
  suggestedPriority: "low" | "medium" | "high" | "urgent";
  dealId?: string | null;
  createdAt: string;
}

interface TeamUser { id: string; name: string; email: string }

const PRIORITY_STYLES: Record<AiSuggestion["suggestedPriority"], string> = {
  urgent: "border-red-300 text-red-700 dark:border-red-900 dark:text-red-400",
  high: "border-amber-300 text-amber-700 dark:border-amber-900 dark:text-amber-400",
  medium: "",
  low: "text-muted-foreground",
};

/**
 * AI-suggested tasks panel shown at the top of the Tasks page.
 *
 * Accepting a suggestion can hand it to whoever should actually do it. That
 * matters most for meeting suggestions: they are all created against the
 * recording's owner, so without a way to reassign, an action item that is
 * plainly someone else's stays stuck in one person's list — which is the thing
 * that made this feature feel broken.
 *
 * Two buttons rather than one menu, deliberately. Taking it yourself is the
 * common case and stays a single click; assigning is its own affordance beside
 * it. Folding both into one dropdown would have slowed the common case down to
 * tidy up the rare one.
 */
export function TaskAISuggestions() {
  const queryClient = useQueryClient();
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: suggestions = [] } = useQuery<AiSuggestion[]>({
    queryKey: ["/api/ai-suggestions"],
  });

  // Only fetched once the panel is actually on screen — no point pulling the
  // directory for a page with nothing to assign.
  const { data: users = [] } = useQuery<TeamUser[]>({
    queryKey: ["users", "for-assignment"],
    queryFn: () => fetch("/api/users", { credentials: "include" }).then((r) => r.json()),
    enabled: suggestions.length > 0,
    staleTime: 5 * 60_000,
  });

  const acceptMutation = useMutation({
    mutationFn: async ({ id, assignToUserId }: { id: string; assignToUserId?: string }) => {
      // Plain fetch rather than apiRequest: that helper throws
      // `400: {"error":"..."}` verbatim, and the server's refusal of a
      // deactivated user is worth showing as a sentence, not a status line.
      const res = await fetch(`/api/ai-suggestions/${id}/accept`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(assignToUserId ? { assignToUserId } : {}),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || "Could not add that task");
      return body;
    },
    onMutate: ({ id }) => { setPendingId(id); setError(null); },
    onSettled: () => setPendingId(null),
    onError: (e: Error) => setError(e.message),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/ai-suggestions"] });
      queryClient.invalidateQueries({ queryKey: ["/api/tasks"] });
    },
  });

  const dismissMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest("POST", `/api/ai-suggestions/${id}/dismiss`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/ai-suggestions"] });
    },
  });

  if (suggestions.length === 0) return null;

  return (
    <Card className="mb-6 bg-gradient-to-r from-primary/5 to-secondary/5 border-primary/20">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-primary-text" />
          Suggested for You
          <Badge variant="secondary" className="ml-2">{suggestions.length}</Badge>
        </CardTitle>
      </CardHeader>

      {error && (
        <div className="mx-6 mb-3 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">
          <AlertCircle className="h-4 w-4 shrink-0" /> {error}
        </div>
      )}

      <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {suggestions.map((suggestion) => {
          const busy = pendingId === suggestion.id;
          return (
            <div
              key={suggestion.id}
              className="bg-background rounded-lg p-4 border"
              data-testid={`ai-suggestion-card-${suggestion.id}`}
            >
              <p className="font-medium text-sm mb-1">{suggestion.title}</p>
              {suggestion.quotedText && (
                <p className="text-xs text-muted-foreground mb-2 line-clamp-2">
                  "{suggestion.quotedText}"
                </p>
              )}
              <div className="flex items-center justify-between gap-2">
                <div className="flex min-w-0 items-center gap-1.5">
                  <Badge variant="outline" className="text-xs shrink-0">
                    {suggestion.source === "email" ? "From email" : "From meeting"}
                  </Badge>
                  {suggestion.suggestedPriority !== "medium" && (
                    <Badge variant="outline"
                           className={`text-xs shrink-0 ${PRIORITY_STYLES[suggestion.suggestedPriority]}`}>
                      {suggestion.suggestedPriority}
                    </Badge>
                  )}
                </div>

                <div className="flex shrink-0 gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2"
                    title="Add to my tasks"
                    onClick={() => acceptMutation.mutate({ id: suggestion.id })}
                    disabled={busy}
                    data-testid={`ai-suggestion-accept-${suggestion.id}`}
                  >
                    {busy
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : <Plus className="w-3.5 h-3.5" />}
                  </Button>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        size="sm" variant="ghost" className="h-7 px-2"
                        title="Assign to someone else" disabled={busy}
                        data-testid={`ai-suggestion-assign-${suggestion.id}`}
                      >
                        <UserPlus className="w-3.5 h-3.5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                        Assign this task to
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      {users.length === 0 && (
                        <DropdownMenuItem disabled className="text-xs">Loading team…</DropdownMenuItem>
                      )}
                      {users.map((u) => (
                        <DropdownMenuItem
                          key={u.id}
                          onClick={() => acceptMutation.mutate({
                            id: suggestion.id, assignToUserId: u.id,
                          })}
                        >
                          <span className="truncate">{u.name}</span>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>

                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2 text-muted-foreground"
                    title="Dismiss"
                    onClick={() => dismissMutation.mutate(suggestion.id)}
                    disabled={dismissMutation.isPending || busy}
                  >
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
